// ============================================================================
// Pérdidas (#1177): dinero que salió y no se puede justificar
// ============================================================================
// Dos orígenes:
//   1. NÓMINA SIN HORAS JUSTIFICADAS. El sueldo paga una jornada (capacidad
//      semanal ÷ 5 × días hábiles). Las horas que la persona registró en Horas
//      Trabajadas la justifican; lo que falta hasta la jornada pagada es
//      pérdida. Se cruza por persona y mes: las horas de más de alguien NO
//      compensan el faltante de otra persona.
//   2. OTRAS PÉRDIDAS. Cualquier gasto marcado `loss` con su motivo
//      (suscripción sin uso, cobro indebido, multa, fraude, tipo de cambio…).
//
// Invariante de la nómina (lo que pide el dueño): por persona y mes,
//   horas justificadas + horas no justificadas = horas pagadas,
// y en un mes cerrado, justificado + pérdida = sueldo del mes.
//
// Quien no lleva seguimiento de horas (ventas, diseño…) NO es pérdida ni
// justificado: va aparte como «sin seguimiento». «No sé» no es «perdí».
// ============================================================================

import {
  BASE_CURRENCY,
  isIsoDate,
  isoDayOf,
  lastDayOf,
  monthRange,
  payrollKey,
  periodOf,
  rosterMonthlySalary,
  round2,
  type Expense,
  type RosterMemberLike,
} from "./index";
import type { PaidPayrollLike } from "./payrollHistory";

/** Jornada semanal por omisión cuando la ficha no trae `weeklyCapacityHours`. */
export const DEFAULT_WEEKLY_CAPACITY_HOURS = 40;

export const LOSS_REASONS = ["sin_uso", "cobro_indebido", "multa_recargo", "fraude", "tipo_de_cambio", "otro"] as const;
export type LossReason = (typeof LOSS_REASONS)[number];

export const LOSS_REASON_LABELS: Record<LossReason, { es: string; en: string }> = {
  sin_uso: { es: "Suscripción o compra sin uso", en: "Unused subscription or purchase" },
  cobro_indebido: { es: "Cobro indebido o duplicado", en: "Wrong or duplicate charge" },
  multa_recargo: { es: "Multa o recargo", en: "Fine or late fee" },
  fraude: { es: "Fraude", en: "Fraud" },
  tipo_de_cambio: { es: "Diferencia por tipo de cambio", en: "Exchange-rate difference" },
  otro: { es: "Otra pérdida", en: "Other loss" },
};

/** Clave de la serie de nómina en las gráficas de pérdidas. */
export const PAYROLL_LOSS_KEY = "nomina_sin_justificar";

export function normalizeLossReason(value: unknown): LossReason | null {
  return LOSS_REASONS.includes(value as LossReason) ? (value as LossReason) : null;
}

/** Campos de pérdida de un gasto, normalizados: sin `loss` no hay motivo ni nota. */
export function normalizeLoss(input: { loss?: unknown; lossReason?: unknown; lossNote?: unknown }): {
  loss: boolean;
  lossReason: LossReason | null;
  lossNote: string | null;
} {
  const loss = input.loss === true;
  if (!loss) return { loss: false, lossReason: null, lossNote: null };
  const note = input.lossNote ? String(input.lossNote).trim().slice(0, 500) : "";
  return { loss: true, lossReason: normalizeLossReason(input.lossReason) || "otro", lossNote: note || null };
}

// --- Nómina × horas ---------------------------------------------------------

export interface PayrollHoursMember extends RosterMemberLike {
  lastName?: string | null;
  /** Horas de la jornada semanal que paga el sueldo (40 por omisión). */
  weeklyCapacityHours?: number | null;
}

export interface PayrollHoursInput {
  members: readonly PayrollHoursMember[];
  /** Horas registradas por ficha y mes: `{ [teamMemberId]: { "2026-09": 152.5 } }`. */
  hours: Record<string, Record<string, number>>;
  /** Fichas que SÍ llevan seguimiento de horas (tienen identidad ligada y activa). */
  tracked: readonly string[];
  /** Meses `YYYY-MM`. */
  from: string;
  to: string;
  /** Hoy (`YYYY-MM-DD`): el mes en curso solo cuenta hasta este día. */
  today: string;
  /** Pesos por unidad de cada moneda (`{ USD: 18.4 }`). */
  exchangeRates?: Record<string, number | null | undefined>;
  /** Tipo de cambio de cada mes; si se da, manda sobre `exchangeRates` (#1255). */
  rateFor?: (currency: string, period: string) => number | null | undefined;
  /** Recibos/liquidaciones pagados: suplen el sueldo de una ficha que no lo tiene capturado. */
  paid?: readonly PaidPayrollLike[];
  /** Meses cuyas horas no se pudieron leer completas: no se cruzan (ni justificado ni pérdida). */
  skipPeriods?: readonly string[];
}

export interface PayrollHoursCell {
  period: string;
  /** Horas de jornada que cubre el sueldo, hasta hoy si el mes está en curso. */
  paidHours: number;
  /** Horas registradas (pueden pasar de las pagadas). */
  actualHours: number;
  justifiedHours: number;
  unjustifiedHours: number;
  /** Horas registradas por encima de la jornada pagada (no compensan a nadie). */
  extraHours: number;
  /** Sueldo del mes en su moneda. */
  salary: number;
  /** Sueldo ÷ horas de jornada del mes completo. */
  hourlyCost: number;
  justifiedCost: number;
  lossCost: number;
  /** Jornada del mes que aún no transcurre (mes en curso). */
  pendingCost: number;
}

export interface PayrollHoursPerson {
  teamMemberId: string;
  name: string;
  currency: string;
  tracked: boolean;
  paidHours: number;
  actualHours: number;
  justifiedHours: number;
  unjustifiedHours: number;
  extraHours: number;
  salary: number;
  justifiedCost: number;
  lossCost: number;
  /** En pesos; `null` si falta el tipo de cambio de su moneda. */
  lossBase: number | null;
  months: PayrollHoursCell[];
}

export interface PayrollHoursMonth {
  period: string;
  inProgress: boolean;
  paidHours: number;
  justifiedHours: number;
  unjustifiedHours: number;
  /** Jornada pagada a quien no lleva seguimiento de horas. */
  untrackedHours: number;
  /** En pesos (solo lo que tiene tipo de cambio). */
  justifiedBase: number;
  lossBase: number;
  untrackedBase: number;
  pendingBase: number;
  /** Personas cuya moneda no tiene tipo de cambio: sus montos no entran en los `*Base`. */
  withoutRate: number;
}

export interface PayrollHoursResult {
  months: PayrollHoursMonth[];
  byPerson: PayrollHoursPerson[];
  totals: Omit<PayrollHoursMonth, "period" | "inProgress"> & { trackedPeople: number; untrackedPeople: number };
}

/** Días hábiles (lunes a viernes) entre dos fechas ISO, ambas incluidas. */
export function businessDaysBetween(from: string, to: string): number {
  if (!isIsoDate(from) || !isIsoDate(to) || from > to) return 0;
  let n = 0;
  const end = Date.parse(`${to}T00:00:00Z`);
  for (let t = Date.parse(`${from}T00:00:00Z`); t <= end; t += 86_400_000) {
    const wd = new Date(t).getUTCDay();
    if (wd !== 0 && wd !== 6) n += 1;
  }
  return n;
}

const maxIso = (a: string, b: string) => (a > b ? a : b);
const minIso = (a: string, b: string) => (a < b ? a : b);

function personName(m: PayrollHoursMember): string {
  return [m.name, m.lastName].filter(Boolean).join(" ") || m.id;
}

/**
 * Cruce de la nómina con las horas registradas, por persona y mes.
 * Entran las mismas fichas que la nómina estimada del padrón: activas, con
 * sueldo fijo capturado, desde su mes de ingreso y hasta su baja.
 */
export function payrollHoursJustification(input: PayrollHoursInput): PayrollHoursResult {
  const current = isIsoDate(input.today) ? periodOf(input.today) : input.to;
  const last = current < input.to ? current : input.to;
  const skip = new Set(input.skipPeriods || []);
  const periods = (input.from <= last ? monthRange(input.from, last) : []).filter((p) => !skip.has(p));
  const tracked = new Set(input.tracked);
  const paid = new Map<string, PaidPayrollLike[]>();
  for (const p of input.paid || []) {
    const key = payrollKey(p.teamMemberId, p.period);
    paid.set(key, [...(paid.get(key) || []), p]);
  }
  // Tipo de cambio del MES (#1255): con `rateFor`, el vigente en cada mes; sin
  // él, el único de `exchangeRates` para todo el rango (comportamiento anterior).
  const rateOf = (currency: string, period: string): number | null => {
    if (currency === BASE_CURRENCY) return 1;
    const r = Number(input.rateFor ? input.rateFor(currency, period) : input.exchangeRates?.[currency]);
    return r > 0 ? r : null;
  };

  const monthMap = new Map<string, PayrollHoursMonth>(
    periods.map((period) => [
      period,
      {
        period,
        inProgress: period === current,
        paidHours: 0,
        justifiedHours: 0,
        unjustifiedHours: 0,
        untrackedHours: 0,
        justifiedBase: 0,
        lossBase: 0,
        untrackedBase: 0,
        pendingBase: 0,
        withoutRate: 0,
      },
    ]),
  );
  const byPerson: PayrollHoursPerson[] = [];

  for (const m of input.members) {
    if (m.payMode !== "fixed_salary") continue;
    const inactive = m.active === false || (m.status && m.status !== "active");
    // Inactivo sin fecha de baja: no se le inventa nómina (misma regla que #1028).
    if (inactive && !isIsoDate(m.endDate)) continue;
    const roster = rosterMonthlySalary(m);
    const since = [m.hireDate, isoDayOf(m.createdAt)].find((d) => isIsoDate(d)) as string | undefined;
    const weekly = Number(m.weeklyCapacityHours) > 0 ? Number(m.weeklyCapacityHours) : DEFAULT_WEEKLY_CAPACITY_HOURS;
    const perDay = weekly / 5;
    const isTracked = tracked.has(m.id);
    const person: PayrollHoursPerson = {
      teamMemberId: m.id,
      name: personName(m),
      currency: roster.currency,
      tracked: isTracked,
      paidHours: 0,
      actualHours: 0,
      justifiedHours: 0,
      unjustifiedHours: 0,
      extraHours: 0,
      salary: 0,
      justifiedCost: 0,
      lossCost: 0,
      lossBase: 0,
      months: [],
    };
    let lossBase = 0;
    let missingRate = false;

    for (const period of periods) {
      if (since && period < periodOf(since)) continue;
      if (isIsoDate(m.endDate) && period > periodOf(m.endDate)) continue;
      const rate = rateOf(roster.currency, period);
      // El sueldo del mes es el del padrón. Un recibo pagado NO lo sustituye: a
      // media quincena solo hay un recibo (medio sueldo) y uno con fecha de pago
      // en el mes siguiente inflaría ese mes. Los recibos solo suplen a una
      // ficha sin sueldo capturado.
      const receipts = (paid.get(payrollKey(m.id, period)) || []).filter((p) => p.currency === roster.currency);
      const salary = roster.amount != null ? roster.amount : receipts.length ? round2(receipts.reduce((s, p) => s + p.amount, 0)) : null;
      if (salary == null || !(salary > 0)) continue;

      const first = `${period}-01`;
      const start = since && periodOf(since) === period ? maxIso(first, since) : first;
      const monthEnd = lastDayOf(period);
      const end = isIsoDate(m.endDate) && periodOf(m.endDate) === period ? minIso(monthEnd, m.endDate) : monthEnd;
      const fullHours = perDay * businessDaysBetween(start, end);
      if (!(fullHours > 0)) continue;
      const elapsedEnd = period === current ? minIso(end, input.today) : end;
      const paidHours = round2(perDay * businessDaysBetween(start, elapsedEnd));
      const hourlyCost = salary / fullHours;
      const actualHours = isTracked ? round2(Math.max(0, Number(input.hours[m.id]?.[period]) || 0)) : 0;
      const justifiedHours = isTracked ? Math.min(actualHours, paidHours) : 0;
      const unjustifiedHours = isTracked ? round2(paidHours - justifiedHours) : 0;
      const cell: PayrollHoursCell = {
        period,
        paidHours,
        actualHours,
        justifiedHours: round2(justifiedHours),
        unjustifiedHours,
        extraHours: isTracked ? round2(Math.max(0, actualHours - paidHours)) : 0,
        salary: round2(salary),
        hourlyCost: round2(hourlyCost),
        justifiedCost: 0,
        lossCost: 0,
        pendingCost: 0,
      };
      // Sin deriva de centavos: la pérdida es lo devengado menos lo justificado
      // y lo pendiente es el sueldo menos lo devengado, así las tres partes
      // suman el sueldo exacto.
      const accrued = paidHours >= fullHours ? round2(salary) : round2(paidHours * hourlyCost);
      cell.justifiedCost = isTracked ? Math.min(accrued, round2(justifiedHours * hourlyCost)) : 0;
      cell.lossCost = isTracked ? round2(accrued - cell.justifiedCost) : 0;
      cell.pendingCost = round2(round2(salary) - accrued);
      person.months.push(cell);
      person.paidHours = round2(person.paidHours + cell.paidHours);
      person.actualHours = round2(person.actualHours + cell.actualHours);
      person.justifiedHours = round2(person.justifiedHours + cell.justifiedHours);
      person.unjustifiedHours = round2(person.unjustifiedHours + cell.unjustifiedHours);
      person.extraHours = round2(person.extraHours + cell.extraHours);
      person.salary = round2(person.salary + cell.salary);
      person.justifiedCost = round2(person.justifiedCost + cell.justifiedCost);
      person.lossCost = round2(person.lossCost + cell.lossCost);

      const month = monthMap.get(period)!;
      if (isTracked) {
        month.paidHours = round2(month.paidHours + cell.paidHours);
        month.justifiedHours = round2(month.justifiedHours + cell.justifiedHours);
        month.unjustifiedHours = round2(month.unjustifiedHours + cell.unjustifiedHours);
      } else {
        month.untrackedHours = round2(month.untrackedHours + cell.paidHours);
      }
      if (rate == null) missingRate = true;
      else lossBase += cell.lossCost * rate;
      if (rate == null) {
        // Solo cuenta lo que SÍ es una pérdida que no se pudo pasar a pesos.
        if (isTracked && cell.lossCost > 0) month.withoutRate += 1;
      } else if (isTracked) {
        month.justifiedBase = round2(month.justifiedBase + cell.justifiedCost * rate);
        month.lossBase = round2(month.lossBase + cell.lossCost * rate);
        month.pendingBase = round2(month.pendingBase + cell.pendingCost * rate);
      } else {
        month.untrackedBase = round2(month.untrackedBase + accrued * rate);
        month.pendingBase = round2(month.pendingBase + cell.pendingCost * rate);
      }
    }
    if (!person.months.length) continue;
    person.lossBase = missingRate ? null : round2(lossBase);
    byPerson.push(person);
  }

  const months = [...monthMap.values()];
  const totals = months.reduce(
    (t, m) => ({
      paidHours: round2(t.paidHours + m.paidHours),
      justifiedHours: round2(t.justifiedHours + m.justifiedHours),
      unjustifiedHours: round2(t.unjustifiedHours + m.unjustifiedHours),
      untrackedHours: round2(t.untrackedHours + m.untrackedHours),
      justifiedBase: round2(t.justifiedBase + m.justifiedBase),
      lossBase: round2(t.lossBase + m.lossBase),
      untrackedBase: round2(t.untrackedBase + m.untrackedBase),
      pendingBase: round2(t.pendingBase + m.pendingBase),
      withoutRate: t.withoutRate + m.withoutRate,
      trackedPeople: t.trackedPeople,
      untrackedPeople: t.untrackedPeople,
    }),
    {
      paidHours: 0,
      justifiedHours: 0,
      unjustifiedHours: 0,
      untrackedHours: 0,
      justifiedBase: 0,
      lossBase: 0,
      untrackedBase: 0,
      pendingBase: 0,
      withoutRate: 0,
      trackedPeople: byPerson.filter((p) => p.tracked).length,
      untrackedPeople: byPerson.filter((p) => !p.tracked).length,
    },
  );
  byPerson.sort((a, b) => (b.lossBase ?? 0) - (a.lossBase ?? 0) || a.name.localeCompare(b.name));
  return { months, byPerson, totals };
}

// --- Resumen de pérdidas (nómina + otras) -----------------------------------

export interface LossMonth {
  period: string;
  inProgress: boolean;
  /** Nómina pagada sin horas que la justifiquen, en pesos. */
  payroll: number;
  /** Gastos marcados como pérdida, en pesos. */
  other: number;
  byReason: Partial<Record<LossReason, number>>;
  total: number;
}

export interface LossSummary {
  months: LossMonth[];
  payroll: number;
  other: number;
  byReason: Partial<Record<LossReason, number>>;
  total: number;
  /** Gastos marcados como pérdida en el rango. */
  count: number;
  /** Pérdidas sin tipo de cambio: no entran en los totales en pesos. */
  withoutRate: number;
}

type LossExpense = Pick<Expense, "date" | "amountBase"> & { loss?: boolean | null; lossReason?: string | null; kind?: string | null; personal?: boolean | null };

/**
 * Pérdidas por mes: la nómina no justificada (si se pasa el cruce) más los
 * gastos marcados `loss`. Las devoluciones y lo personal no cuentan.
 */
export function lossSummary(input: { expenses: readonly LossExpense[]; payroll?: PayrollHoursResult | null; from: string; to: string }): LossSummary {
  const periods = input.from <= input.to ? monthRange(input.from, input.to) : [];
  const map = new Map<string, LossMonth>(periods.map((period) => [period, { period, inProgress: false, payroll: 0, other: 0, byReason: {}, total: 0 }]));
  for (const m of input.payroll?.months || []) {
    const row = map.get(m.period);
    if (!row) continue;
    row.payroll = m.lossBase;
    row.inProgress = m.inProgress;
  }
  let count = 0;
  let withoutRate = input.payroll?.totals.withoutRate || 0;
  const byReason: Partial<Record<LossReason, number>> = {};
  for (const e of input.expenses) {
    if (e.loss !== true || e.kind === "devolucion" || e.personal === true) continue;
    const row = isIsoDate(e.date) ? map.get(periodOf(e.date)) : undefined;
    if (!row) continue;
    count += 1;
    if (e.amountBase == null) {
      withoutRate += 1;
      continue;
    }
    const reason = normalizeLossReason(e.lossReason) || "otro";
    row.other = round2(row.other + e.amountBase);
    row.byReason[reason] = round2((row.byReason[reason] || 0) + e.amountBase);
    byReason[reason] = round2((byReason[reason] || 0) + e.amountBase);
  }
  const months = [...map.values()].map((m) => ({ ...m, total: round2(m.payroll + m.other) }));
  const payroll = round2(months.reduce((s, m) => s + m.payroll, 0));
  const other = round2(months.reduce((s, m) => s + m.other, 0));
  return { months, payroll, other, byReason, total: round2(payroll + other), count, withoutRate };
}
